/**
 * 桌面授权状态 —— 填店铺名即用 + 本地 7 天试用 + 联网拉正式到期。
 * 仅**单机版** Tauri 启用;云版 SaaS / 浏览器 / vite 开发跳过门(云租户靠运营后台控权)。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ActivationHttpError, heartbeatActivation, registerActivation } from '@/api/activation'
import {
  getMachineFingerprint,
  isTauri,
  readDesktopLicense,
  writeDesktopLicense,
  type DesktopLicenseState
} from '@/composables/useDesktopBridge'
import { isLocalDesktop } from '@/edition'

export const TRIAL_DAYS = 7
export const GRACE_DAYS = 10

export type LicenseUiStatus = 'active' | 'grace' | 'expired' | 'pending'

export const useLicenseStore = defineStore('license', () => {
  const ready = ref(false)
  /** 非单机版 Tauri → 不启用授权门 */
  const enabled = ref(false)
  const shopName = ref('')
  const fingerprint = ref('')
  const trialStartedAt = ref<string | null>(null)
  const formalExpiresAt = ref<string | null>(null)
  const syncError = ref<string | null>(null)

  function effectiveExpiresAt(): Date | null {
    if (formalExpiresAt.value) return new Date(formalExpiresAt.value)
    if (trialStartedAt.value) {
      const t = new Date(trialStartedAt.value)
      t.setDate(t.getDate() + TRIAL_DAYS)
      return t
    }
    return null
  }

  const status = computed<LicenseUiStatus>(() => {
    if (!enabled.value) return 'active'
    if (!trialStartedAt.value) return 'pending'
    const exp = effectiveExpiresAt()
    if (!exp) return 'pending'
    const now = Date.now()
    const expMs = exp.getTime()
    if (now < expMs) return 'active'
    if (now < expMs + GRACE_DAYS * 86400000) return 'grace'
    return 'expired'
  })

  const readonly = computed(() => status.value === 'expired')
  const daysUntilExpiry = computed(() => {
    const exp = effectiveExpiresAt()
    if (!exp) return 0
    return Math.floor((exp.getTime() - Date.now()) / 86400000)
  })

  async function bootstrap() {
    // 云版 / 浏览器:不做本机授权门(SaaS 租户由运营后台开通/停用)。
    if (!isTauri() || !isLocalDesktop()) {
      enabled.value = false
      ready.value = true
      return
    }
    enabled.value = true
    try {
      const existing = await readDesktopLicense()
      if (existing) {
        shopName.value = existing.shopName
        fingerprint.value = existing.fingerprint
        trialStartedAt.value = existing.trialStartedAt
        formalExpiresAt.value = existing.expiresAt
      }
      if (!fingerprint.value) {
        fingerprint.value = await getMachineFingerprint()
      }
    } catch (e) {
      syncError.value = e instanceof Error ? e.message : String(e)
    } finally {
      ready.value = true
    }
    // 已激活则后台心跳(不挡 UI)
    if (shopName.value && fingerprint.value) {
      void syncWithCloud()
    }
  }

  async function persist() {
    if (!enabled.value || !shopName.value || !fingerprint.value || !trialStartedAt.value) return
    const state: DesktopLicenseState = {
      shopName: shopName.value,
      fingerprint: fingerprint.value,
      trialStartedAt: trialStartedAt.value,
      expiresAt: formalExpiresAt.value,
      lastSyncedAt: new Date().toISOString()
    }
    await writeDesktopLicense(state)
  }

  /** 首次填店铺名 → 本地立刻开用 + 写文件 + 尝试上报 */
  async function activate(name: string) {
    const trimmed = name.trim()
    if (!trimmed) throw new Error('请填写店铺名或公司名')
    if (trimmed.length > 80) throw new Error('名称过长')
    if (!fingerprint.value) {
      fingerprint.value = await getMachineFingerprint()
    }
    shopName.value = trimmed
    if (!trialStartedAt.value) {
      trialStartedAt.value = new Date().toISOString()
    }
    await persist()
    void syncWithCloud()
  }

  async function syncWithCloud() {
    if (!enabled.value || !fingerprint.value || !shopName.value || !trialStartedAt.value) return
    try {
      let res
      try {
        res = await registerActivation({
          fingerprint: fingerprint.value,
          shop_name: shopName.value,
          trial_started_at: trialStartedAt.value
        })
      } catch (e) {
        // 被限流(429)时 heartbeat 也必然 429,别再打一发白吃限流窗口;
        // 其余错误(如本地缓存 trial 过旧被 register 400 拒)才值得试 heartbeat。
        if (e instanceof ActivationHttpError && e.status === 429) throw e
        res = await heartbeatActivation(fingerprint.value)
      }
      // 旧版缓存可能仍是明文 MachineGuid;服务端迁移记录后返回规范 SHA-256，
      // 覆盖本地缓存，后续心跳不再上传原始机器标识。
      if (res.fingerprint) fingerprint.value = res.fingerprint
      trialStartedAt.value = res.trial_started_at
      formalExpiresAt.value = res.expires_at
      syncError.value = null
      await persist()
    } catch (e) {
      // 完全离线:保持本地缓存,试用照常
      syncError.value = e instanceof Error ? e.message : String(e)
    }
  }

  return {
    ready,
    enabled,
    shopName,
    fingerprint,
    trialStartedAt,
    formalExpiresAt,
    syncError,
    status,
    readonly,
    daysUntilExpiry,
    effectiveExpiresAt,
    bootstrap,
    activate,
    syncWithCloud
  }
})
